import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Nav, Pagination, Row, Spinner, Table } from 'react-bootstrap';
import { logoutUser } from '../store/slices/authSlice';
import {
  approveDriverAccount,
  clearAdminError,
  fetchAdminCustomers,
  fetchDriversForApproval,
  rejectDriverAccount,
  setCustomerActiveStatus,
  setDriverActiveStatus,
} from '../store/slices/adminSlice';
import AppTopbar from '../components/AppTopbar';
import AdminApprovalRulesPanel from '../components/admin/AdminApprovalRulesPanel';
import AdminFinancePanel from '../components/admin/AdminFinancePanel';
import AdminSupportPanel from '../components/admin/AdminSupportPanel';
import AdminEmailPanel from '../components/admin/AdminEmailPanel';
import AdminDriverVehicles from '../components/admin/AdminDriverVehicles';
import axiosInstance, { API_BASE_URL } from '../config/axiosConfig';
import { APP_NAME, ROUTES } from '../config/appConfig';
import { DRIVER_DOCUMENT_TYPES, hasAllDriverDocuments } from '../utils/driverDocuments';
import { getErrorMessage, normalizeUserRole } from '../utils/apiResponseUtils';
import { formatDateTime } from '../utils/dataUtils';
import '../styles/AdminDashboard.css';

const ADMIN_PAGE_SIZE = 20;
const API_ORIGIN = (() => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch {
    return '';
  }
})();

const getDisplayName = (record) => (
  [record?.firstName, record?.lastName].filter(Boolean).join(' ') || 'Not provided'
);

const formatCurrency = (amount) => (
  `Rs ${Number(amount || 0).toFixed(2)}`
);

const StatusBadge = ({ active, activeLabel = 'Active', inactiveLabel = 'Inactive' }) => (
  <Badge bg={active ? 'success' : 'secondary'}>
    {active ? activeLabel : inactiveLabel}
  </Badge>
);

const AdminPagination = ({ pagination, pageState, onPageChange, loading }) => {
  const totalPages = Math.max(1, Number(pagination?.totalPages || 1));
  const currentPage = Math.min(Number(pageState.page || 0), totalPages - 1);
  const totalElements = Number(pagination?.totalElements || 0);

  return (
    <div className="admin-pagination-row">
      <span>
        Page {currentPage + 1} of {totalPages} - {totalElements} total
      </span>
      <Pagination size="sm" className="mb-0">
        <Pagination.First disabled={loading || currentPage === 0} onClick={() => onPageChange(0)} />
        <Pagination.Prev disabled={loading || currentPage === 0} onClick={() => onPageChange(currentPage - 1)} />
        <Pagination.Item active>{currentPage + 1}</Pagination.Item>
        <Pagination.Next disabled={loading || currentPage >= totalPages - 1} onClick={() => onPageChange(currentPage + 1)} />
        <Pagination.Last disabled={loading || currentPage >= totalPages - 1} onClick={() => onPageChange(totalPages - 1)} />
      </Pagination>
    </div>
  );
};

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const {
    drivers,
    customers,
    driversPagination,
    customersPagination,
    loading,
    customersLoading,
    error,
    customersError,
    approvalLoadingId,
    activeStatusLoadingId,
  } = useSelector((state) => state.admin);
  const [managementTab, setManagementTab] = useState('customers');
  const [driversPage, setDriversPage] = useState({ page: 0, size: ADMIN_PAGE_SIZE });
  const [customersPage, setCustomersPage] = useState({ page: 0, size: ADMIN_PAGE_SIZE });
  const [activeToggleConfirmation, setActiveToggleConfirmation] = useState(null);
  const [reviewReasons, setReviewReasons] = useState({});
  const [documentError, setDocumentError] = useState('');
  const [documentLoadingId, setDocumentLoadingId] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(ROUTES.adminLogin);
      return;
    }

    if (normalizeUserRole(user) !== 'admin') {
      navigate(ROUTES.adminLogin);
      return;
    }

  }, [isAuthenticated, navigate, user]);

  useEffect(() => {
    if (isAuthenticated && normalizeUserRole(user) === 'admin') {
      dispatch(fetchDriversForApproval(driversPage));
    }
  }, [dispatch, driversPage, isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && normalizeUserRole(user) === 'admin') {
      dispatch(fetchAdminCustomers(customersPage));
    }
  }, [customersPage, dispatch, isAuthenticated, user]);

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate(ROUTES.adminLogin);
  };

  const refreshDriversPage = () => dispatch(fetchDriversForApproval(driversPage));
  const refreshCustomersPage = () => dispatch(fetchAdminCustomers(customersPage));

  const openActiveToggleConfirmation = (type, record) => {
    setActiveToggleConfirmation({
      type,
      record,
      nextActive: !Boolean(record.active),
    });
  };

  const handleToggleDriverActive = async (driver) => {
    const nextActive = !Boolean(driver.active);
    const result = await dispatch(setDriverActiveStatus({ driverId: driver.id, active: nextActive }));

    if (!result.error) {
      await refreshDriversPage();
    }
  };

  const handleToggleCustomerActive = async (customer) => {
    const nextActive = !Boolean(customer.active);
    const result = await dispatch(setCustomerActiveStatus({ customerId: customer.id, active: nextActive }));

    if (!result.error) {
      refreshCustomersPage();
    }
  };

  const handleConfirmActiveToggle = async () => {
    if (!activeToggleConfirmation) {
      return;
    }

    const { type, record } = activeToggleConfirmation;

    if (type === 'driver') {
      await handleToggleDriverActive(record);
    } else {
      await handleToggleCustomerActive(record);
    }

    setActiveToggleConfirmation(null);
  };

  const getSafeDocumentUrl = (url) => {
    if (!url) {
      return '';
    }

    const trimmedUrl = String(url).trim();

    if (/^https?:\/\//i.test(trimmedUrl)) {
      try {
        const parsedUrl = new URL(trimmedUrl);
        return parsedUrl.origin === API_ORIGIN ? parsedUrl.href : '';
      } catch {
        return '';
      }
    }

    const safePath = trimmedUrl.startsWith('/') ? trimmedUrl : `/${trimmedUrl}`;
    return `${API_BASE_URL}${safePath}`;
  };

  const handleViewDocument = async (driver, documentType, document) => {
    const fallbackUrl = '/admin/drivers/' + encodeURIComponent(driver.id) + '/documents/' + encodeURIComponent(document?.id || documentType.apiType);
    const resolvedUrl = getSafeDocumentUrl(document?.fileUrl || document?.downloadUrl || document?.dataUrl || fallbackUrl);
    if (!resolvedUrl) {
      setDocumentError('The document URL must use the configured API environment.');
      return;
    }
    const viewer = window.open('', '_blank');
    if (!viewer) {
      setDocumentError('Allow pop-ups to view driver documents.');
      return;
    }
    viewer.opener = null;
    setDocumentError('');
    setDocumentLoadingId(driver.id + '-' + documentType.key);
    try {
      const response = await axiosInstance.get(resolvedUrl, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      viewer.location.replace(blobUrl);
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      viewer.close();
      setDocumentError(getErrorMessage(error, 'Unable to open the document. Please try again.'));
    } finally {
      setDocumentLoadingId('');
    }
  };

  const pendingDrivers = drivers.filter((driver) => driver.approvalStatus !== 'APPROVED');
  const approvedDrivers = drivers.filter((driver) => driver.approvalStatus === 'APPROVED');
  const totalDrivers = driversPagination.totalElements || drivers.length;
  const totalCustomers = customersPagination.totalElements || customers.length;

  return (
    <div className="admin-dashboard-shell">
      {documentError && <Alert variant="danger" dismissible onClose={() => setDocumentError('')}>{documentError}</Alert>}
      <AppTopbar
        brandHref={ROUTES.adminDashboard}
        title={APP_NAME}
        subtitle="Admin Portal"
        navId="admin-navbar"
        className="admin-topbar"
        pillText="Approval Desk"
        pillClassName="admin-sync-pill"
        userText={user?.username || user?.firstName || 'Admin'}
        onLogout={handleLogout}
      />

      <Container className="py-4 py-lg-5">
        <section className="admin-hero mb-4 mb-lg-5">
          <div>
            <span className="hero-kicker">Approval Workflow</span>
            <h1 className="hero-title">Approve driver access before trips go live.</h1>
            <p className="hero-subtitle">
              Drivers can sign up and log in immediately, but they cannot see trip details until admin approval is complete.
            </p>
          </div>
          <div className="hero-chip-row">
            <div className="hero-chip admin-chip">
              <span className="hero-chip-label">Pending</span>
              <strong>{pendingDrivers.length}</strong>
            </div>
            <div className="hero-chip admin-chip">
              <span className="hero-chip-label">Approved</span>
              <strong>{approvedDrivers.length}</strong>
            </div>
            <div className="hero-chip admin-chip">
              <span className="hero-chip-label">Drivers</span>
              <strong>{totalDrivers}</strong>
            </div>
            <div className="hero-chip admin-chip">
              <span className="hero-chip-label">Customers</span>
              <strong>{totalCustomers}</strong>
            </div>
          </div>
        </section>

        {error && (
          <Alert variant="danger" dismissible onClose={() => dispatch(clearAdminError())}>
            {error}
          </Alert>
        )}

        <Row className="g-4">
          <Col lg={7}>
            <div className="admin-panel">
              <div className="section-heading mb-4">
                <div className="admin-section-heading-row">
                  <div>
                    <h2>Pending Driver Approvals</h2>
                    <p>Review driver identity, vehicle, and license data before unlocking trip visibility.</p>
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={refreshDriversPage}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                </div>
              </div>

              {loading && drivers.length === 0 ? (
                <div className="admin-empty-state">
                  <Spinner animation="border" />
                </div>
              ) : pendingDrivers.length === 0 ? (
                <div className="admin-empty-state">
                  <h3>No pending drivers</h3>
                  <p>Every registered driver is currently approved.</p>
                </div>
              ) : (
                <div className="driver-review-grid">
                  {pendingDrivers.map((driver) => {
                    const documents = driver.documents || {};
                    const documentsComplete = hasAllDriverDocuments(documents);
                    const reviewReason = reviewReasons[driver.id] ?? driver.rejectionReason ?? '';

                    return (
                      <Card key={driver.id} className="admin-driver-card">
                        <Card.Body>
                          <div className="admin-driver-card-header">
                            <div>
                              <span className="summary-eyebrow">Driver Review</span>
                              <h3>{driver.firstName} {driver.lastName}</h3>
                            </div>
                            <Badge bg="warning" text="dark">{driver.approvalStatus}</Badge>
                          </div>
                          <dl className="admin-driver-meta">
                            <div>
                              <dt>Email</dt>
                              <dd>{driver.email || 'Not provided'}</dd>
                            </div>
                            <div>
                              <dt>Phone</dt>
                              <dd>{driver.phone || 'Not provided'}</dd>
                            </div>
                            <div>
                              <dt>Vehicle Number</dt>
                              <dd>{driver.vehicleNumber || 'Not provided'}</dd>
                            </div>
                            <div>
                              <dt>License Number</dt>
                              <dd>{driver.licenseNumber || 'Not provided'}</dd>
                            </div>
                          </dl>
                          <div className="admin-driver-proof-strip">
                            {DRIVER_DOCUMENT_TYPES.map((documentType) => {
                              const document = documents[documentType.key];

                              return (
                                <div className="admin-driver-proof-pill" key={documentType.key}>
                                  <div className="admin-driver-proof-head">
                                    <span className="admin-driver-proof-label">{documentType.label}</span>
                                    <Badge bg={document ? 'success' : 'secondary'}>{document ? 'Uploaded' : 'Missing'}</Badge>
                                  </div>
                                  <strong>{document?.name || 'No document uploaded'}</strong>
                                  <small>
                                    {document?.uploadedAt
                                      ? `Uploaded ${formatDateTime(document.uploadedAt)}`
                                      : 'Waiting for driver upload'}
                                  </small>
                                  {document && (
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      disabled={Boolean(documentLoadingId)}
                                      onClick={() => handleViewDocument(driver, documentType, document)}
                                    >
                                      View Document
                                    </Button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          {!documentsComplete && (
                            <p className="admin-validation-helper">Approval requires Aadhar, DL, and RC. Enter a reason to reject the documents.</p>
                          )}
                          <Form.Group className="admin-review-reason mb-3">
                            <Form.Label>Admin Reason</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={reviewReason}
                              placeholder="Add a reason for approval or rejection"
                              onChange={(event) => setReviewReasons((current) => ({
                                ...current,
                                [driver.id]: event.target.value,
                              }))}
                            />
                          </Form.Group>
                          <div className="admin-review-actions">
                            <Button
                              variant="success"
                              disabled={Boolean(approvalLoadingId) || !documentsComplete}
                              onClick={() => dispatch(approveDriverAccount({ driver, reason: reviewReason }))}
                            >
                              {approvalLoadingId === driver.id ? 'Approving...' : 'Approve'}
                            </Button>
                            <Button
                              variant="outline-danger"
                              disabled={Boolean(approvalLoadingId) || !reviewReason.trim()}
                              onClick={() => dispatch(rejectDriverAccount({ driver, reason: reviewReason }))}
                            >
                              {approvalLoadingId === driver.id ? 'Rejecting...' : 'Reject'}
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </Col>

          <Col lg={5}>
            <AdminApprovalRulesPanel approvedDriverCount={approvedDrivers.length} /><AdminFinancePanel/>
          </Col>
        </Row>

        <AdminSupportPanel/><AdminEmailPanel/>
        <div className="admin-panel admin-management-panel mt-4">
          <div className="section-heading mb-4">
            <div className="admin-section-heading-row">
              <div>
                <h2>User Management</h2>
                <p>Manage customer and driver access independently with paginated backend tables.</p>
              </div>
              <div className="admin-panel-actions">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={managementTab === 'customers' ? refreshCustomersPage : refreshDriversPage}
                  disabled={managementTab === 'customers' ? customersLoading : loading}
                >
                  {(managementTab === 'customers' ? customersLoading : loading) ? 'Loading...' : 'Refresh'}
                </Button>
              </div>
            </div>
          </div>

          <Nav
            variant="pills"
            activeKey={managementTab}
            onSelect={(selectedKey) => setManagementTab(selectedKey || 'customers')}
            className="admin-management-tabs"
          >
            <Nav.Item>
              <Nav.Link eventKey="customers">Customers</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="drivers">Drivers</Nav.Link>
            </Nav.Item>
          </Nav>

          {managementTab === 'customers' ? (
            <>
              {customersError && customers.length === 0 && <Alert variant="danger">{customersError}</Alert>}
              <div className="admin-table-wrap">
                <Table responsive hover className="admin-serviceability-table admin-management-table align-middle">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Active</th>
                      <th>Wallet Balance</th>
                      <th>Email Verified</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customersLoading && customers.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="admin-table-empty">
                          <Spinner animation="border" size="sm" /> Loading customers...
                        </td>
                      </tr>
                    ) : customersError && customers.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="admin-table-empty">{customersError}</td>
                      </tr>
                    ) : customers.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="admin-table-empty">No customers found.</td>
                      </tr>
                    ) : (
                      customers.map((customer) => (
                        <tr key={customer.id}>
                          <td><strong>{getDisplayName(customer)}</strong></td>
                          <td>{customer.email || '-'}</td>
                          <td>{customer.phone || '-'}</td>
                          <td><StatusBadge active={customer.active} /></td>
                          <td>{formatCurrency(customer.walletBalance)}</td>
                          <td><StatusBadge active={customer.emailVerified} activeLabel="Verified" inactiveLabel="Unverified" /></td>
                          <td>{formatDateTime(customer.createdAt)}</td>
                          <td>
                            <div className="admin-table-actions">
                              <Button
                                variant={customer.active ? 'outline-danger' : 'outline-success'}
                                size="sm"
                                disabled={activeStatusLoadingId === `customer-${customer.id}`}
                                onClick={() => openActiveToggleConfirmation('customer', customer)}
                              >
                                {activeStatusLoadingId === `customer-${customer.id}`
                                  ? 'Updating...'
                                  : customer.active ? 'Deactivate' : 'Activate'}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              <AdminPagination
                pagination={customersPagination}
                pageState={customersPage}
                loading={customersLoading}
                onPageChange={(page) => setCustomersPage((current) => ({ ...current, page }))}
              />
            </>
          ) : (
            <>
              {error && <Alert variant="danger">{error}</Alert>}
              <div className="admin-table-wrap">
                <Table responsive hover className="admin-serviceability-table admin-management-table align-middle">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Vehicle Number</th>
                      <th>License Number</th>
                      <th>Active</th>
                      <th>Available</th>
                      <th>Approval / Document Status</th>
                      <th>Documents</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && drivers.length === 0 ? (
                      <tr>
                        <td colSpan="11" className="admin-table-empty">
                          <Spinner animation="border" size="sm" /> Loading drivers...
                        </td>
                      </tr>
                    ) : drivers.length === 0 ? (
                      <tr>
                        <td colSpan="11" className="admin-table-empty">No drivers found.</td>
                      </tr>
                    ) : (
                      drivers.map((driver) => (
                        <tr key={driver.id}>
                          <td><strong>{getDisplayName(driver)}</strong></td>
                          <td>{driver.email || '-'}</td>
                          <td>{driver.phone || '-'}</td>
                          <td>{driver.vehicleNumber || '-'}<div className="mt-2"><AdminDriverVehicles driver={driver}/></div></td>
                          <td>{driver.licenseNumber || '-'}</td>
                          <td><StatusBadge active={driver.active} /></td>
                          <td><StatusBadge active={driver.available} activeLabel="Available" inactiveLabel="Unavailable" /></td>
                          <td>
                            <Badge bg={driver.approvalStatus === 'APPROVED' ? 'success' : driver.approvalStatus === 'REJECTED' ? 'danger' : 'warning'} text={driver.approvalStatus === 'PENDING' ? 'dark' : undefined}>
                              {driver.approvalStatus}
                            </Badge>
                            {driver.approvalStatus === 'REJECTED' && driver.approvalReason && (
                              <small className="d-block mt-1">{driver.approvalReason}</small>
                            )}
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-2">
                              {DRIVER_DOCUMENT_TYPES.map((documentType) => {
                                const document = driver.documents?.[documentType.key];
                                return (
                                  <div key={documentType.key}>
                                    <strong>{documentType.apiType}: </strong>
                                    {document ? (
                                      <>
                                        <span>{document.fileName || document.name || 'Uploaded document'}</span>{' '}
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          disabled={Boolean(documentLoadingId)}
                                          onClick={() => handleViewDocument(driver, documentType, document)}
                                          aria-label={'View ' + documentType.apiType + ' for ' + getDisplayName(driver)}
                                        >
                                          {documentLoadingId === driver.id + '-' + documentType.key ? 'Opening...' : 'View'}
                                        </Button>
                                      </>
                                    ) : <span className="text-muted">Not uploaded</span>}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                          <td>{formatDateTime(driver.createdAt)}</td>
                          <td>
                            <div className="admin-table-actions">
                              <Button
                                variant={driver.active ? 'outline-danger' : 'outline-success'}
                                size="sm"
                                disabled={activeStatusLoadingId === `driver-${driver.id}`}
                                onClick={() => openActiveToggleConfirmation('driver', driver)}
                              >
                                {activeStatusLoadingId === `driver-${driver.id}`
                                  ? 'Updating...'
                                  : driver.active ? 'Deactivate' : 'Activate'}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              <AdminPagination
                pagination={driversPagination}
                pageState={driversPage}
                loading={loading}
                onPageChange={(page) => setDriversPage((current) => ({ ...current, page }))}
              />
            </>
          )}
        </div>

      </Container>

      <Modal
        show={Boolean(activeToggleConfirmation)}
        onHide={() => setActiveToggleConfirmation(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {activeToggleConfirmation?.nextActive ? 'Activate Account' : 'Deactivate Account'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Are you sure you want to {activeToggleConfirmation?.nextActive ? 'activate' : 'deactivate'}{' '}
            this {activeToggleConfirmation?.type} account?
          </p>
          {activeToggleConfirmation?.record && (
            <div className="admin-confirmation-target">
              <strong>{getDisplayName(activeToggleConfirmation.record)}</strong>
              <span>{activeToggleConfirmation.record.email || activeToggleConfirmation.record.phone || 'No contact provided'}</span>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setActiveToggleConfirmation(null)}
            disabled={Boolean(activeStatusLoadingId)}
          >
            Cancel
          </Button>
          <Button
            variant={activeToggleConfirmation?.nextActive ? 'success' : 'danger'}
            onClick={handleConfirmActiveToggle}
            disabled={Boolean(activeStatusLoadingId)}
          >
            {activeStatusLoadingId
              ? 'Updating...'
              : activeToggleConfirmation?.nextActive ? 'Activate' : 'Deactivate'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminDashboard;
